(() => {
  const preference = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const connection = window.navigator?.connection;
  const pausedKey = 'mechtech.heroPaused';
  const controllers = new Set();
  const teamControllers = new Set();
  const initializedHeroes = new WeakSet();
  const initializedTeamVideos = new WeakSet();
  const initializedReveals = new WeakSet();
  const supportsObserver = typeof window.IntersectionObserver === 'function';
  let reducedMotion = Boolean(preference?.matches);

  const savedPause = () => {
    try { return window.sessionStorage.getItem(pausedKey) === 'true'; }
    catch { return false; }
  };
  const savePause = value => {
    try {
      if (value) window.sessionStorage.setItem(pausedKey, 'true');
      else window.sessionStorage.removeItem(pausedKey);
    } catch { /* Playback controls also work when storage is unavailable. */ }
  };
  const pageHidden = () => document.hidden || document.visibilityState === 'hidden';

  function initializeHero(hero) {
    if (initializedHeroes.has(hero)) return;
    initializedHeroes.add(hero);
    hero.classList.toggle('motion-enabled', !reducedMotion);
    const video = hero.querySelector('video[data-hero-video]');
    const button = hero.querySelector('[data-hero-video-toggle]');
    if (!video || !button) return;
    const sources = Array.from(video.querySelectorAll('[data-hero-video-source]'))
      .filter(source => source.dataset.src);
    if (!sources.length) return;

    const label = button.querySelector('[data-hero-video-label]');
    const playIcon = button.querySelector('[data-video-play-icon]');
    const pauseIcon = button.querySelector('[data-video-pause-icon]');
    let manualPause = savedPause();
    let explicitPlay = false;
    let inView = !supportsObserver;
    let loaded = false;
    let failed = false;
    let playing = false;
    let blocked = false;
    let pending = false;
    let attempt = 0;
    let destroyed = false;
    let heroObserver;

    const updateControl = () => {
      const text = playing ? button.dataset.pauseLabel : button.dataset.playLabel;
      if (label) label.textContent = text;
      if (text) button.setAttribute('aria-label', text);
      if (playIcon) playIcon.hidden = playing;
      if (pauseIcon) pauseIcon.hidden = !playing;
      button.hidden = failed;
    };
    const shouldPlay = () => !destroyed && !failed && !manualPause && inView && !pageHidden()
      && (explicitPlay || (!reducedMotion && !connection?.saveData && !blocked));

    const pause = () => {
      attempt += 1;
      pending = false;
      if (loaded) video.pause();
      playing = false;
      updateControl();
    };
    const play = () => {
      if (!shouldPlay() || playing || pending) return;
      if (!loaded) {
        loaded = true;
        sources.forEach(source => source.setAttribute('src', source.dataset.src));
        video.muted = true;
        video.load();
      }
      if (failed) return;
      const thisAttempt = ++attempt;
      pending = true;
      const rejected = () => {
        if (thisAttempt !== attempt || failed) return;
        pending = false;
        blocked = true;
        explicitPlay = false;
        playing = false;
        updateControl();
      };
      try {
        const result = video.play();
        if (result?.then) result.then(() => {
          if (thisAttempt === attempt) pending = false;
        }, rejected);
        else pending = false;
      } catch { rejected(); }
    };
    const sync = () => { if (shouldPlay()) play(); else pause(); };

    video.addEventListener('playing', () => {
      if (!shouldPlay()) { pause(); return; }
      pending = false;
      playing = true;
      hero.dataset.videoReady = 'true';
      updateControl();
    });
    video.addEventListener('pause', () => {
      playing = false;
      updateControl();
    });
    video.addEventListener('ended', () => {
      playing = false;
      updateControl();
    });
    video.addEventListener('error', () => {
      failed = true;
      pause();
      hero.dataset.videoReady = 'false';
    });
    button.addEventListener('click', () => {
      if (failed) return;
      if (playing) {
        manualPause = true;
        explicitPlay = false;
        savePause(true);
        pause();
      } else {
        manualPause = false;
        explicitPlay = true;
        blocked = false;
        savePause(false);
        play();
      }
    });

    const controller = {
      hero,
      sync,
      destroy() {
        destroyed = true;
        pause();
        heroObserver?.disconnect();
        controllers.delete(controller);
      },
      preferenceChanged() {
        hero.classList.toggle('motion-enabled', !reducedMotion);
        // A newly enabled system preference wins until the visitor presses Play again.
        if (reducedMotion || connection?.saveData) explicitPlay = false;
        sync();
      }
    };
    controllers.add(controller);
    updateControl();
    if (supportsObserver) {
      heroObserver = new window.IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.target !== hero) return;
          inView = entry.isIntersecting;
          sync();
        });
      }, { threshold: 0 });
      heroObserver.observe(hero);
    } else sync();
  }

  function initializeTeamVideo(video) {
    if (initializedTeamVideos.has(video)) return;
    initializedTeamVideos.add(video);
    // JavaScript owns autoplay so offscreen videos and motion/data preferences
    // are respected before playback starts. Native controls remain available.
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    let inView = !supportsObserver;
    let manualPause = false;
    let explicitPlay = false;
    let blocked = false;
    let pending = false;
    let destroyed = false;
    let attempt = 0;
    let expectedPauses = 0;
    const expectedPlays = new Set();
    let observer;

    const shouldPlay = () => !destroyed && !manualPause && inView && !pageHidden()
      && (explicitPlay || (!reducedMotion && !connection?.saveData && !blocked));
    const pause = () => {
      attempt += 1;
      pending = false;
      if (video.paused) return;
      expectedPauses += 1;
      video.pause();
    };
    const play = () => {
      if (!shouldPlay() || pending || !video.paused) return;
      const thisAttempt = ++attempt;
      const request = {};
      expectedPlays.add(request);
      pending = true;
      const rejected = () => {
        expectedPlays.delete(request);
        if (thisAttempt !== attempt) return;
        pending = false;
        blocked = true;
      };
      try {
        const result = video.play();
        if (result?.then) result.then(() => {
          if (thisAttempt === attempt) pending = false;
        }, rejected);
        else pending = false;
      } catch { rejected(); }
    };
    const sync = () => { if (shouldPlay()) play(); else pause(); };

    video.addEventListener('play', () => {
      const automatic = expectedPlays.values().next();
      if (!automatic.done) expectedPlays.delete(automatic.value);
      else {
        // A play event without our own request came from the native controls.
        manualPause = false;
        explicitPlay = true;
        blocked = false;
      }
      if (!shouldPlay()) pause();
    });
    video.addEventListener('playing', () => {
      pending = false;
      if (!shouldPlay()) pause();
    });
    video.addEventListener('pause', () => {
      if (expectedPauses) { expectedPauses -= 1; return; }
      manualPause = true;
      explicitPlay = false;
      attempt += 1;
      pending = false;
    });
    video.addEventListener('error', () => { blocked = true; pause(); });

    const controller = {
      video,
      sync,
      preferenceChanged() {
        if (reducedMotion || connection?.saveData) explicitPlay = false;
        sync();
      },
      destroy() {
        destroyed = true;
        pause();
        observer?.disconnect();
        teamControllers.delete(controller);
      }
    };
    teamControllers.add(controller);
    if (supportsObserver) {
      observer = new window.IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.target !== video) return;
          inView = entry.isIntersecting;
          sync();
        });
      }, { threshold: 0 });
      observer.observe(video);
    } else sync();
  }

  let revealObserver;
  function initializeReveals(scope) {
    scope.querySelectorAll('[data-motion-reveal]').forEach(target => {
      if (initializedReveals.has(target)) return;
      initializedReveals.add(target);
      if (reducedMotion || !supportsObserver) {
        target.classList.add('is-revealed');
        return;
      }
      if (!revealObserver) {
        revealObserver = new window.IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          });
        }, { threshold: 0.08 });
      }
      revealObserver.observe(target);
    });
  }

  function initialize(scope = document) {
    document.documentElement.classList.toggle('racing-motion-enabled', !reducedMotion);
    scope.querySelectorAll('[data-racing-hero]').forEach(initializeHero);
    scope.querySelectorAll('video[data-team-video]').forEach(initializeTeamVideo);
    initializeReveals(scope);
  }
  function preferenceChanged() {
    reducedMotion = Boolean(preference?.matches);
    document.documentElement.classList.toggle('racing-motion-enabled', !reducedMotion);
    if (reducedMotion) {
      revealObserver?.disconnect();
      document.querySelectorAll('[data-motion-reveal]').forEach(target => target.classList.add('is-revealed'));
    }
    document.querySelectorAll('[data-racing-hero]').forEach(hero => hero.classList.toggle('motion-enabled', !reducedMotion));
    controllers.forEach(controller => controller.preferenceChanged());
    teamControllers.forEach(controller => controller.preferenceChanged());
  }

  document.addEventListener('visibilitychange', () => controllers.forEach(controller => controller.sync()));
  document.addEventListener('visibilitychange', () => teamControllers.forEach(controller => controller.sync()));
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    controllers.forEach(controller => {
      if (event.target === controller.hero || event.target.contains(controller.hero)) controller.destroy();
    });
    teamControllers.forEach(controller => {
      if (event.target === controller.video || event.target.contains(controller.video)) controller.destroy();
    });
    event.target.querySelectorAll('[data-motion-reveal]').forEach(target => revealObserver?.unobserve(target));
  });
  if (preference?.addEventListener) preference.addEventListener('change', preferenceChanged);
  else preference?.addListener?.(preferenceChanged);
  connection?.addEventListener?.('change', preferenceChanged);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
})();
